import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { InstitutionLayout } from '@/components/layout/InstitutionLayout';
import { PlatformLayout } from '@/components/platform/PlatformLayout';
import { SeoHead } from '@/components/seo/SeoHead';
import { HomePage } from '@/pages/HomePage';
import { ProductsPage } from '@/pages/ProductsPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { InstitutionAdminLayout } from '@/pages/admin/InstitutionAdminLayout';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { AdminProfilePage } from '@/pages/admin/AdminProfilePage';
import { AdminCampusesPage } from '@/pages/admin/AdminCampusesPage';
import { AdminAcademicYearsPage } from '@/pages/admin/AdminAcademicYearsPage';
import { AdminAcademicStructurePage } from '@/pages/admin/AdminAcademicStructurePage';
import { ApprovalsPage } from '@/pages/ApprovalsPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminRolesPage } from '@/pages/admin/AdminRolesPage';
import { AdminOrganisationPage } from '@/pages/admin/AdminOrganisationPage';
import { AdminProductsPage } from '@/pages/admin/AdminProductsPage';
import { AdminBrandingPage } from '@/pages/admin/AdminBrandingPage';
import { AdminIntegrationsPage } from '@/pages/admin/AdminIntegrationsPage';
import { AdminTasksPage } from '@/pages/admin/AdminTasksPage';
import { AdminReportsPage } from '@/pages/admin/AdminReportsPage';
import { AdminAnnouncementsPage } from '@/pages/admin/AdminAnnouncementsPage';
import { AdminServicesPage } from '@/pages/admin/AdminServicesPage';
import { AdminFormSchemasPage } from '@/pages/admin/AdminFormSchemasPage';
import { AdminFormSchemaDetailPage } from '@/pages/admin/AdminFormSchemaDetailPage';
import { AdminWorkflowsPage } from '@/pages/admin/AdminWorkflowsPage';
import { WorkflowDesignerPage } from '@/pages/admin/workflows/WorkflowDesignerPage';
import { AdminDataImportPage } from '@/pages/admin/AdminDataImportPage';
import { AdminMarketplacePage } from '@/pages/admin/AdminMarketplacePage';
import { AdminParentAccessPage } from '@/pages/admin/AdminParentAccessPage';
import { AdminSecurityPage } from '@/pages/admin/AdminSecurityPage';
import { AdminAuditPage } from '@/pages/admin/AdminAuditPage';
import { AdminSupportPage } from '@/pages/admin/AdminSupportPage';
import { AdminExperiencePage } from '@/pages/admin/AdminExperiencePage';
import { AdminCredentialsPage } from '@/pages/admin/AdminCredentialsPage';
import { VerifyPage } from '@/pages/VerifyPage';
import { JourneyPage } from '@/pages/JourneyPage';
import { PlatformDashboardPage } from '@/pages/platform/PlatformDashboardPage';
import { OnboardingCasesPage } from '@/pages/platform/OnboardingCasesPage';
import { OnboardingCaseDetailPage } from '@/pages/platform/OnboardingCaseDetailPage';
import { InstitutionsPage } from '@/pages/platform/InstitutionsPage';
import { InstitutionDetailPage } from '@/pages/platform/InstitutionDetailPage';
import { GroupsPage } from '@/pages/platform/GroupsPage';
import { GroupDetailPage } from '@/pages/platform/GroupDetailPage';
import { AuditPage } from '@/pages/platform/AuditPage';
import { DelegationsPage } from '@/pages/platform/DelegationsPage';
import { SupportAccessPage } from '@/pages/platform/SupportAccessPage';
import { FeatureFlagsPage } from '@/pages/platform/FeatureFlagsPage';
import { PlatformConnectionsPage } from '@/pages/platform/PlatformConnectionsPage';
import { PlatformConnectionDetailPage } from '@/pages/platform/PlatformConnectionDetailPage';
import { InstitutionIntegrationsPage } from '@/pages/platform/InstitutionIntegrationsPage';
import { ConfigurationStudioPage } from '@/pages/platform/ConfigurationStudioPage';
import { InstitutionTemplatesPage } from '@/pages/platform/InstitutionTemplatesPage';
import { InstitutionTemplateDetailPage } from '@/pages/platform/InstitutionTemplateDetailPage';
import { GradingStrategiesPage } from '@/pages/platform/GradingStrategiesPage';
import { GradingStrategyDetailPage } from '@/pages/platform/GradingStrategyDetailPage';
import { AcademicPoliciesPage } from '@/pages/platform/AcademicPoliciesPage';
import { AcademicPolicyDetailPage } from '@/pages/platform/AcademicPolicyDetailPage';
import { FormSchemasPage } from '@/pages/platform/FormSchemasPage';
import { FormSchemaDetailPage } from '@/pages/platform/FormSchemaDetailPage';
import { IntegrationHealthPage } from '@/pages/platform/IntegrationHealthPage';
import { IntegrationDetailPage } from '@/pages/platform/IntegrationDetailPage';
import { PoliciesPage } from '@/pages/platform/PoliciesPage';
import { MasterDataPage } from '@/pages/platform/MasterDataPage';
import { OperationsPage } from '@/pages/platform/OperationsPage';
import { ImportsPage } from '@/pages/platform/ImportsPage';
import { ReportsPage } from '@/pages/platform/ReportsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Two applications, one bundle.
 *
 * Everything under /admin is the CSKY platform console and renders in its own
 * shell; everything else is the institution workspace. They are siblings rather
 * than one shell with conditional menu items, because a platform administrator is
 * doing a different job on different data and needs to be able to tell, at every
 * moment, which of the two they are looking at.
 *
 * PlatformLayout is also the guard for its subtree — no /admin route can be
 * reached without passing through it. That check is presentation only; every
 * endpoint behind it verifies the role against a signed token.
 *
 * Several console screens are scoped to one institution and take it as a
 * `?institutionId=` query parameter rather than a path segment. That is
 * deliberate: they are cross-cutting tools an operator arrives at with an
 * institution already in mind, and making the id part of the path would imply a
 * hierarchy — /institutions/:id/audit — that does not hold, because the same
 * screens are also used with no institution at all.
 */
export function App() {
  return (
    <BrowserRouter>
      <SeoHead />
      <Routes>
        <Route path="/admin" element={<PlatformLayout />}>
          <Route index element={<PlatformDashboardPage />} />
          <Route path="onboarding" element={<OnboardingCasesPage />} />
          <Route path="onboarding/:caseId" element={<OnboardingCaseDetailPage />} />
          <Route path="institutions" element={<InstitutionsPage />} />
          {/* Legacy bookmarks are redirected into the single canonical onboarding
              case workflow. They must never reach the old synchronous creator. */}
          <Route path="institutions/new" element={<Navigate to="/admin/onboarding" replace />} />
          <Route path="institutions/:institutionId" element={<InstitutionDetailPage />} />
          {/* F1/F2 — institution groups. The detail route takes the id in the
              path (there is no "group without an institution", the way audit or
              imports have "no institution"), and its tabs live in the query
              string so a tab is bookmarkable without owning a route. */}
          <Route path="groups" element={<GroupsPage />} />
          <Route path="groups/:groupId" element={<GroupDetailPage />} />
          {/* An institution's mappings live under the institution, not under
              integrations. The identifiers that say which EduCore school this is
              belong to the school; the connection they point at does not. */}
          <Route
            path="institutions/:institutionId/integrations"
            element={<InstitutionIntegrationsPage />}
          />
          {/* The Configuration Studio. The canonical URL is under the institution —
              an operating model belongs to a school — while the bare `configuration`
              route is the chooser plus the shared platform layers, which belong to
              no single institution. Same split as the integrations area above. */}
          <Route
            path="institutions/:institutionId/configuration"
            element={<ConfigurationStudioPage />}
          />
          <Route path="configuration" element={<ConfigurationStudioPage />} />

          {/* The hierarchy template designer: read and preview only. Templates are
              migration-seeded reference data, so this pair of routes writes nothing —
              the "customised" preview endpoint it calls persists nothing either. */}
          <Route path="institution-templates" element={<InstitutionTemplatesPage />} />
          <Route path="institution-templates/:code" element={<InstitutionTemplateDetailPage />} />

          {/* The three configuration-governance registers (CFG-11/12/13).
              Flat under /admin rather than under an institution, because each
              register mixes platform catalogue entries with per-institution
              variants — the scope is a filter on the screen, not a segment of
              the URL. Detail routes carry the version's UUID: versions are
              rows, and two versions of one code are two different pages. */}
          <Route path="grading-strategies" element={<GradingStrategiesPage />} />
          <Route path="grading-strategies/:strategyId" element={<GradingStrategyDetailPage />} />
          <Route path="academic-policies" element={<AcademicPoliciesPage />} />
          <Route path="academic-policies/:policyId" element={<AcademicPolicyDetailPage />} />
          <Route path="form-schemas" element={<FormSchemasPage />} />
          <Route path="form-schemas/:schemaId" element={<FormSchemaDetailPage />} />

          <Route path="audit" element={<AuditPage />} />
          <Route path="delegations" element={<DelegationsPage />} />
          <Route path="support-access" element={<SupportAccessPage />} />

          <Route path="feature-flags" element={<FeatureFlagsPage />} />
          {/* Three tabs over one area. Connections are environment-level and are
              reached without choosing an institution — which is the whole point of
              the split, because "is the Digital Switch up?" is not a question about
              any one school. `integrations/mappings` is the tab with no institution
              chosen yet; once one is, the canonical URL is under that institution.

              The static segments are declared alongside the legacy
              `:integrationId` route and win over it by React Router's ranking, so
              /admin/integrations/health is the health tab rather than a lookup for
              a registration whose id happens to be the word "health". */}
          <Route path="integrations" element={<PlatformConnectionsPage />} />
          <Route path="integrations/mappings" element={<InstitutionIntegrationsPage />} />
          <Route path="integrations/health" element={<IntegrationHealthPage />} />
          <Route
            path="integrations/connections/:connectionId"
            element={<PlatformConnectionDetailPage />}
          />
          {/* The registry these screens replace. Still served, still holding real
              rows, and still linked from the institution tab for the length of the
              migration — a bookmark to one must not 404 while records are syncing
              under it. */}
          <Route path="integrations/:integrationId" element={<IntegrationDetailPage />} />
          <Route path="policies" element={<PoliciesPage />} />
          <Route path="master-data" element={<MasterDataPage />} />

          <Route path="operations" element={<OperationsPage />} />
          <Route path="imports" element={<ImportsPage />} />
          <Route path="reports" element={<ReportsPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route path="/admin/institution" element={<InstitutionAdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="profile" element={<AdminProfilePage />} />
          <Route path="campuses" element={<AdminCampusesPage />} />
          <Route path="academic-years" element={<AdminAcademicYearsPage />} />
          <Route path="academic-structure" element={<AdminAcademicStructurePage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="roles" element={<AdminRolesPage />} />
          {/* Identity credentials (cards / mobile identities), beside the other
              people-management pages. */}
          <Route path="credentials" element={<AdminCredentialsPage />} />
          {/* Separate from /roles, which is still the flat assignment screen. */}
          <Route path="organisation" element={<AdminOrganisationPage />} />
          {/* Vocabulary and capability packs — what this institution's screens say
              and show. Kept apart from /organisation, which is about people and
              offices; this is about the product's voice and surface area. */}
          <Route path="experience" element={<AdminExperiencePage />} />
          <Route path="products" element={<AdminProductsPage />} />
          <Route path="branding" element={<AdminBrandingPage />} />
          <Route path="integrations" element={<AdminIntegrationsPage />} />
          <Route path="tasks" element={<AdminTasksPage />} />
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="announcements" element={<AdminAnnouncementsPage />} />
          <Route path="services" element={<AdminServicesPage />} />
          <Route path="form-schemas" element={<AdminFormSchemasPage />} />
          <Route path="form-schemas/:schemaId" element={<AdminFormSchemaDetailPage />} />
          <Route path="workflows" element={<AdminWorkflowsPage />} />
          {/* The maker-checker designer: one process, its versions, its steps. */}
          <Route path="workflows/:processKey" element={<WorkflowDesignerPage />} />
          <Route path="data-import" element={<AdminDataImportPage />} />
          <Route path="marketplace" element={<AdminMarketplacePage />} />
          <Route path="parent-access" element={<AdminParentAccessPage />} />
          <Route path="security" element={<AdminSecurityPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
          <Route path="support" element={<AdminSupportPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route element={<InstitutionLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          {/* Staff scan console. The workspace, not the admin console, because
              the people who scan — teachers, librarians, gate staff — are not
              administrators; the server checks verification.<purpose> anyway. */}
          <Route path="/verify" element={<VerifyPage />} />
          {/* The journey timeline: own day at /journey, a linked student's day
              at /journey/:personId (guardian scope enforced server-side). */}
          <Route path="/journey" element={<JourneyPage />} />
          <Route path="/journey/:personId" element={<JourneyPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
